(function () {
  'use strict';

  var DISMISS_KEY = 'colorful-bunch-newsletter-dismissed-v2';
  var MINIMIZED_KEY = 'colorful-bunch-newsletter-minimized-v1';
  var COMPLETE_KEY = 'colorful-bunch-newsletter-complete-v2';
  var DISMISS_MS = 30 * 24 * 60 * 60 * 1000;
  var COMPLETE_MS = 365 * 24 * 60 * 60 * 1000;
  var REQUEST_TIMEOUT_MS = 30000;
  var OFFER_POLL_TIMEOUT_MS = 20000;
  var WIGGLE_INTERVAL_MS = 60 * 1000;
  var TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  var TURNSTILE_ACTION = 'turnstile-spin-v2';
  var turnstilePromise = null;
  var popupFocusGuards = new WeakMap();

  function checkPopupFocus(root) {
    var guard = popupFocusGuards.get(root);
    if (guard) guard.check();
  }

  function protectOutsideFocus(root) {
    var frame = null;
    var motionUntil = 0;
    var motionObscuredFocus = null;
    var observedFocus = null;
    var stopped = false;
    var resizeObserver;
    var mutationObserver;

    function overlaps(a, b, gap) {
      return a.width > 0 && a.height > 0 && b.width > 0 && b.height > 0 &&
        a.left < b.right + gap && a.right > b.left - gap &&
        a.top < b.bottom + gap && a.bottom > b.top - gap;
    }

    function check() {
      if (stopped) return;
      var focused = document.activeElement;
      // Includes the root's pointer-minimize handoff and embedded challenges.
      var inside = focused && root.contains(focused);
      while (focused && focused.shadowRoot && focused.shadowRoot.activeElement) {
        focused = focused.shadowRoot.activeElement;
      }
      if (root.hidden || inside || focused === document.body || focused === document.documentElement) focused = null;
      if (resizeObserver && observedFocus !== focused) {
        if (observedFocus) resizeObserver.unobserve(observedFocus);
        if (focused) resizeObserver.observe(focused);
      }
      observedFocus = focused;
      var obscured = false;
      if (focused && focused.getBoundingClientRect) {
        var target = focused.getBoundingClientRect();
        var style = window.getComputedStyle(focused);
        // Include the offer's hard shadow and the external control's focus ring.
        var gap = 10 + Math.max(4, (parseFloat(style.outlineWidth) || 0) + Math.max(0, parseFloat(style.outlineOffset) || 0));
        var surface = root.querySelector(root.classList.contains('is-minimized') ? '[data-cb-signup-teaser]' : '[data-cb-signup-card]');
        // Fixed-position layout coordinates exclude the opening transform. Check
        // the destination as well as the painted bounds before revealing it.
        var resting = {
          left: root.offsetLeft, top: root.offsetTop,
          right: root.offsetLeft + root.offsetWidth, bottom: root.offsetTop + root.offsetHeight,
          width: root.offsetWidth, height: root.offsetHeight
        };
        obscured = overlaps(target, resting, gap) ||
          (surface && overlaps(target, surface.getBoundingClientRect(), gap));
      }
      // A wiggle can cross the same edge several times. Keep one suspension
      // until it settles, but release immediately when focus changes.
      if (!focused || motionObscuredFocus !== focused || Date.now() >= motionUntil) motionObscuredFocus = null;
      if (obscured && Date.now() < motionUntil) motionObscuredFocus = focused;
      if (motionObscuredFocus) obscured = true;
      // visibility retains geometry and footer spacing, and removes the offer
      // from hit-testing/tab order without touching focus, form state or storage.
      root.classList.toggle('is-focus-obscured', Boolean(obscured));
    }

    function schedule() {
      if (stopped || frame !== null) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        check();
        if (Date.now() < motionUntil) schedule();
      });
    }

    function checkAndSchedule() {
      check();
      // Native focus scrolling and other handlers may change layout afterward.
      schedule();
    }

    function watchMotion() {
      // Opening/closing and wiggle animations are finite. Never poll at idle.
      motionUntil = Date.now() + 800;
      checkAndSchedule();
    }

    document.addEventListener('focusin', checkAndSchedule);
    document.addEventListener('focusout', schedule);
    document.addEventListener('scroll', checkAndSchedule, true);
    document.addEventListener('input', schedule);
    document.addEventListener('change', schedule);
    document.addEventListener('load', schedule, true);
    window.addEventListener('resize', checkAndSchedule);
    window.addEventListener('pageshow', checkAndSchedule);
    root.addEventListener('transitionrun', watchMotion);
    root.addEventListener('animationstart', watchMotion);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkAndSchedule);
      window.visualViewport.addEventListener('scroll', checkAndSchedule);
    }
    if (window.ResizeObserver) {
      resizeObserver = new window.ResizeObserver(checkAndSchedule);
      resizeObserver.observe(root);
      resizeObserver.observe(document.body);
    }
    if (window.MutationObserver) {
      mutationObserver = new window.MutationObserver(checkAndSchedule);
      mutationObserver.observe(root, { attributes: true, childList: true, characterData: true, subtree: true });
      mutationObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-cb-contrast', 'class', 'style'] });
    }
    if (document.fonts) document.fonts.ready.then(schedule);
    popupFocusGuards.set(root, {
      check: check,
      stop: function () {
        stopped = true;
        if (frame !== null) window.cancelAnimationFrame(frame);
        if (resizeObserver) resizeObserver.disconnect();
        if (mutationObserver) mutationObserver.disconnect();
        document.removeEventListener('focusin', checkAndSchedule);
        document.removeEventListener('focusout', schedule);
        document.removeEventListener('scroll', checkAndSchedule, true);
        document.removeEventListener('input', schedule);
        document.removeEventListener('change', schedule);
        document.removeEventListener('load', schedule, true);
        window.removeEventListener('resize', checkAndSchedule);
        window.removeEventListener('pageshow', checkAndSchedule);
        root.removeEventListener('transitionrun', watchMotion);
        root.removeEventListener('animationstart', watchMotion);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', checkAndSchedule);
          window.visualViewport.removeEventListener('scroll', checkAndSchedule);
        }
        popupFocusGuards.delete(root);
      }
    });
  }

  function readUntil(key) {
    try { return Number(window.localStorage.getItem(key) || 0); }
    catch (error) { return 0; }
  }

  function writeUntil(key, duration) {
    try { window.localStorage.setItem(key, String(Date.now() + duration)); }
    catch (error) { /* Signup does not depend on browser storage. */ }
  }

  function isExplicitTestPreview() {
    try { return new URLSearchParams(window.location.search).get('cb_signup_test') === '1'; }
    catch (error) { return false; }
  }

  function idempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.prototype.map.call(bytes, function (value) {
      return value.toString(16).padStart(2, '0');
    }).join('');
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstilePromise) return turnstilePromise;
    turnstilePromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = function () {
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('Turnstile did not initialize'));
      };
      script.onerror = function () { reject(new Error('Turnstile could not load')); };
      document.head.appendChild(script);
    }).catch(function (error) {
      turnstilePromise = null;
      throw error;
    });
    return turnstilePromise;
  }

  async function turnstileToken(form) {
    var turnstile = await loadTurnstile();
    var container = form.querySelector('[data-cb-turnstile]');
    var sitekey = form.getAttribute('data-turnstile-site-key');
    if (!container || !sitekey) throw new Error('Signup protection is not configured');
    container.replaceChildren();
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timeout = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('Signup protection timed out'));
      }, 15000);
      var finish = function (callback) {
        return function (value) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeout);
          callback(value);
        };
      };
      var widgetId = turnstile.render(container, {
        sitekey: sitekey,
        action: TURNSTILE_ACTION,
        execution: 'execute',
        callback: finish(resolve),
        'error-callback': finish(function () { reject(new Error('Signup protection failed')); }),
        'expired-callback': finish(function () { reject(new Error('Signup protection expired')); }),
        'timeout-callback': finish(function () { reject(new Error('Signup protection timed out')); })
      });
      turnstile.execute(widgetId);
    });
  }

  async function postSignup(form, body) {
    var endpoint = form.getAttribute('data-signup-endpoint');
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
    try {
      var response = await window.fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      return { response: response, data: await response.json().catch(function () { return null; }) };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function wait(milliseconds) {
    return new Promise(function (resolve) { window.setTimeout(resolve, milliseconds); });
  }

  async function resolveWelcomeOffer(form, offer) {
    var endpoint = form.getAttribute('data-welcome-offer-endpoint');
    if (!endpoint || !offer || !offer.claim) return null;
    var deadline = Math.min(
      Date.now() + OFFER_POLL_TIMEOUT_MS,
      Number(offer.expiresAt) || Date.now() + OFFER_POLL_TIMEOUT_MS
    );
    var pollAfterMs = Math.max(350, Math.min(3000, Number(offer.pollAfterMs) || 750));
    while (Date.now() < deadline) {
      await wait(pollAfterMs);
      var controller = new AbortController();
      var timeout = window.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS);
      var response;
      var data;
      try {
        response = await window.fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ action: 'resolve', claim: offer.claim }),
          signal: controller.signal
        });
        data = await response.json().catch(function () { return null; });
      } finally {
        window.clearTimeout(timeout);
      }
      if (response.ok && data && data.status === 'ready' && data.offer && data.offer.code) return data;
      if (response.status !== 202 || !data || data.status !== 'waiting') {
        throw { response: response, data: data };
      }
      pollAfterMs = Math.max(350, Math.min(3000, Number(data.pollAfterMs) || pollAfterMs));
    }
    return null;
  }

  function setStatus(form, message, kind) {
    var status = form.querySelector('[data-cb-signup-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
    status.setAttribute('data-kind', kind || 'info');
  }

  function setSubmitting(form, submitting) {
    var button = form.querySelector('[data-cb-signup-submit]');
    var label = form.querySelector('[data-cb-signup-submit-label]');
    if (label && !label.hasAttribute('data-idle-label')) label.setAttribute('data-idle-label', label.textContent);
    if (button) button.disabled = submitting;
    if (label) label.textContent = submitting ? 'Joining…' : label.getAttribute('data-idle-label');
  }

  function popupElements(root) {
    return {
      formView: root && root.querySelector('[data-cb-signup-form-view]'),
      success: root && root.querySelector('[data-cb-signup-success]'),
      heading: root && root.querySelector('[data-cb-signup-success-heading]'),
      copy: root && root.querySelector('[data-cb-signup-success-copy]'),
      spinner: root && root.querySelector('[data-cb-signup-spinner]'),
      codeButton: root && root.querySelector('[data-cb-signup-copy]'),
      code: root && root.querySelector('[data-cb-signup-code]')
    };
  }

  function showPopupPending(root, message) {
    if (!root) return;
    var elements = popupElements(root);
    if (elements.formView) elements.formView.hidden = true;
    if (elements.success) elements.success.hidden = false;
    if (elements.heading) elements.heading.textContent = 'Hooray!';
    if (elements.copy) elements.copy.textContent = message || 'Joining the bunch…';
    if (elements.spinner) elements.spinner.hidden = false;
    if (elements.codeButton) elements.codeButton.hidden = true;
  }

  function restorePopupForm(root) {
    if (!root) return;
    var elements = popupElements(root);
    if (elements.formView) elements.formView.hidden = false;
    if (elements.success) elements.success.hidden = true;
    if (elements.spinner) elements.spinner.hidden = true;
    if (elements.codeButton) elements.codeButton.hidden = true;
    if (elements.code) elements.code.textContent = '';
  }

  function showPopupResult(root, result) {
    if (!root) return;
    root.classList.add('is-complete');
    var elements = popupElements(root);
    if (elements.formView) elements.formView.hidden = true;
    if (elements.success) elements.success.hidden = false;
    if (elements.heading) elements.heading.textContent = 'Hooray!';
    if (elements.spinner) elements.spinner.hidden = true;
    if (result.offer && result.offer.code) {
      if (elements.copy) elements.copy.textContent = 'Take 10% off your first order with code';
      if (elements.code) elements.code.textContent = result.offer.code;
      if (elements.codeButton) elements.codeButton.hidden = false;
    } else if (result.offer && result.offer.status === 'pending') {
      if (elements.copy) elements.copy.textContent = 'You’re subscribed. Your welcome code is on its way by email.';
      if (elements.codeButton) elements.codeButton.hidden = true;
    } else {
      if (elements.copy) elements.copy.textContent = 'You’re subscribed. Thanks for being here.';
      if (elements.codeButton) elements.codeButton.hidden = true;
    }
  }

  function showInlineResult(form, result) {
    var message = 'You’re subscribed. Thanks for being here.';
    if (result.offer && result.offer.code) message = 'You’re in! Your code is ' + result.offer.code + '.';
    else if (result.offer && result.offer.status === 'pending') message = 'You’re subscribed. Your code will arrive by email.';
    setStatus(form, message, 'success');
    var input = form.querySelector('input[type="email"]');
    if (input) input.disabled = true;
    var button = form.querySelector('[data-cb-signup-submit]');
    if (button) button.hidden = true;
  }

  function errorMessage(result) {
    if (result.response && result.response.status === 429) return 'Please wait a minute and try again.';
    if (result.data && result.data.error === 'HUMAN_VERIFICATION_FAILED') return 'We couldn’t verify that signup. Please try again.';
    if (result.data && result.data.error === 'SIGNUP_TEMPORARILY_UNAVAILABLE') return 'Signup is briefly unavailable. Please try again soon.';
    return 'We couldn’t add you just yet. Please try again.';
  }

  function bindForm(form) {
    if (form.getAttribute('data-cb-lifecycle-initialized') === 'true') return;
    form.setAttribute('data-cb-lifecycle-initialized', 'true');
    var active = false;
    var input = form.querySelector('input[type="email"]');
    if (input) {
      var preloadTurnstile = function () { loadTurnstile().catch(function () { /* Submission retries. */ }); };
      input.addEventListener('focus', preloadTurnstile, { once: true });
      input.addEventListener('pointerdown', preloadTurnstile, { once: true });
    }
    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (active) return;
      input = form.querySelector('input[type="email"]');
      if (!input || !input.checkValidity()) {
        if (input) input.reportValidity();
        return;
      }
      active = true;
      var submittedWithFocus = form.contains(document.activeElement);
      var submittedEmail = input.value.trim().toLowerCase();
      setSubmitting(form, true);
      setStatus(form, '', 'info');
      var popup = form.closest('[data-cb-signup]');
      var canRestoreFocus = function () {
        return submittedWithFocus && (!popup || (!popup.hidden && !popup.classList.contains('is-minimized') && !popup.classList.contains('is-focus-obscured'))) &&
          (document.activeElement === document.body || form.contains(document.activeElement) ||
            (popup && popup.contains(document.activeElement)));
      };
      try {
        // Keep the form and any interactive challenge exposed until verification completes.
        var token = await turnstileToken(form);
        var movePendingFocus = canRestoreFocus();
        if (popup) {
          showPopupPending(popup, 'Joining the bunch…');
          if (movePendingFocus) popup.querySelector('[data-cb-signup-success]').focus({ preventScroll: true });
        }
        var result = await postSignup(form, {
          email: submittedEmail,
          turnstileToken: token,
          idempotencyKey: idempotencyKey(),
          source: form.getAttribute('data-signup-source') || 'footer'
        });
        if (!result.response.ok && result.response.status !== 202) throw result;
        if (!result.data || !['ready', 'subscribed', 'already_subscribed'].includes(result.data.status)) throw result;
        writeUntil(COMPLETE_KEY, COMPLETE_MS);
        if (result.data.offer && result.data.offer.status === 'pending' && result.data.offer.claim) {
          if (popup) showPopupPending(popup, 'Making your one-time 10% code…');
          try {
            var resolved = await resolveWelcomeOffer(form, result.data.offer);
            if (resolved) result.data = resolved;
          } catch (offerError) {
            // Consent and enrollment already succeeded. Keep the truthful
            // subscribed state and let the welcome email deliver the code.
          }
        }
        if (popup) showPopupResult(popup, result.data);
        else showInlineResult(form, result.data);
        document.dispatchEvent(new CustomEvent('colorful-bunch:subscribed', { detail: result.data }));
      } catch (error) {
        var moveErrorFocus = canRestoreFocus();
        if (popup) restorePopupForm(popup);
        setStatus(form, errorMessage(error && error.response ? error : { response: null, data: null }), 'error');
        if (moveErrorFocus) input.focus({ preventScroll: true });
      } finally {
        active = false;
        setSubmitting(form, false);
      }
    });
  }

  function close(root, remember) {
    if (remember) writeUntil(DISMISS_KEY, DISMISS_MS);
    root.classList.add('is-leaving');
    root.classList.remove('is-visible');
    checkPopupFocus(root);
    window.setTimeout(function () {
      root.hidden = true;
      root.classList.remove('is-leaving');
      var guard = popupFocusGuards.get(root);
      if (guard) guard.stop();
    }, 320);
  }

  function wiggle(root) {
    if (
      root.hidden ||
      root.classList.contains('is-focus-obscured') ||
      root.classList.contains('is-leaving') ||
      root.classList.contains('is-minimized') ||
      document.hidden ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return;
    root.classList.remove('is-wiggling');
    window.requestAnimationFrame(function () {
      if (root.hidden || root.classList.contains('is-focus-obscured') || root.classList.contains('is-leaving') || root.classList.contains('is-minimized')) return;
      root.classList.add('is-wiggling');
      window.setTimeout(function () { root.classList.remove('is-wiggling'); }, 760);
    });
  }

  function minimize(root, focusReopen, fromPointer) {
    var card = root.querySelector('[data-cb-signup-card]');
    var teaser = root.querySelector('[data-cb-signup-teaser]');
    root.classList.remove('is-wiggling');
    root.classList.add('is-minimized');
    if (card) card.hidden = true;
    if (teaser) {
      teaser.hidden = false;
      var reopenButton = teaser.querySelector('[data-cb-signup-reopen]');
      if (focusReopen && reopenButton) {
        // Keep pointer dismissal in the visible offer without giving its action
        // an automatic keyboard ring. The next Tab reaches the reopen button.
        (fromPointer ? root : reopenButton).focus({ preventScroll: true });
      }
    }
    checkPopupFocus(root);
  }

  function restore(root) {
    var card = root.querySelector('[data-cb-signup-card]');
    var teaser = root.querySelector('[data-cb-signup-teaser]');
    root.classList.remove('is-minimized');
    if (card) card.hidden = false;
    if (teaser) teaser.hidden = true;
    var success = root.querySelector('[data-cb-signup-success]');
    var target = success && !success.hidden ? success : root.querySelector('input[type="email"]');
    if (target) target.focus({ preventScroll: true });
    checkPopupFocus(root);
  }

  function open(root, wiggleAfterOpen) {
    if (root.classList.contains('is-leaving')) return;
    root.hidden = false;
    checkPopupFocus(root);
    window.requestAnimationFrame(function () {
      if (root.hidden || root.classList.contains('is-leaving')) return;
      checkPopupFocus(root);
      window.requestAnimationFrame(function () {
        if (root.hidden || root.classList.contains('is-leaving')) return;
        root.classList.add('is-visible');
        checkPopupFocus(root);
        if (wiggleAfterOpen) window.setTimeout(function () { wiggle(root); }, 560);
      });
    });
  }

  function initializePopup(root) {
    if (root.getAttribute('data-cb-signup-initialized') === 'true') return;
    root.setAttribute('data-cb-signup-initialized', 'true');
    var testPreview = isExplicitTestPreview();
    var designMode = Boolean(window.Shopify && window.Shopify.designMode);
    if (!testPreview && !designMode && root.getAttribute('data-popup-enabled') !== 'true') return;
    if (!testPreview && !designMode && root.getAttribute('data-customer-subscribed') === 'true') return;
    if (!testPreview && !designMode && (readUntil(COMPLETE_KEY) > Date.now() || readUntil(DISMISS_KEY) > Date.now())) return;
    protectOutsideFocus(root);
    var startMinimized = false;
    if (!testPreview && !designMode) {
      try {
        startMinimized = window.localStorage.getItem(MINIMIZED_KEY) === 'true';
        // Count this page immediately, even if navigation beats the opening delay.
        window.localStorage.setItem(MINIMIZED_KEY, 'true');
      } catch (error) { /* Signup does not depend on browser storage. */ }
      window.addEventListener('pageshow', function (event) {
        // Back/forward cache restores the original DOM without initializing again.
        if (!event.persisted || root.classList.contains('is-complete')) return;
        if (readUntil(COMPLETE_KEY) > Date.now() || readUntil(DISMISS_KEY) > Date.now()) {
          window.clearTimeout(openTimeout);
          root.hidden = true;
          root.classList.remove('is-visible');
          var guard = popupFocusGuards.get(root);
          if (guard) guard.stop();
          return;
        }
        minimize(root, false);
      });
    }
    var closeButton = root.querySelector('[data-cb-signup-close]');
    var reopenButton = root.querySelector('[data-cb-signup-reopen]');
    var dismissButton = root.querySelector('[data-cb-signup-dismiss]');
    var copyButton = root.querySelector('[data-cb-signup-copy]');
    if (closeButton) closeButton.addEventListener('click', function (event) {
      if (root.classList.contains('is-complete')) close(root, false);
      else minimize(root, true, event.detail > 0);
    });
    if (reopenButton) reopenButton.addEventListener('click', function () { restore(root); });
    if (dismissButton) dismissButton.addEventListener('click', function (event) {
      event.stopPropagation();
      close(root, true);
    });
    if (copyButton) copyButton.addEventListener('click', async function () {
      var code = root.querySelector('[data-cb-signup-code]');
      var label = root.querySelector('[data-cb-signup-copy-label]');
      if (!code || !code.textContent) return;
      try { await navigator.clipboard.writeText(code.textContent); if (label) label.textContent = 'Copied'; }
      catch (error) { window.getSelection().selectAllChildren(code); }
    });
    if (startMinimized) minimize(root, false);
    var openTimeout = window.setTimeout(function () { open(root, !startMinimized); }, testPreview ? 0 : 3000);
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.setInterval(function () {
        if (root.hidden || document.hidden || root.matches(':hover') || root.matches(':focus-within')) return;
        wiggle(root);
      }, WIGGLE_INTERVAL_MS);
    }
  }

  function initializeAll() {
    document.querySelectorAll('[data-cb-lifecycle-signup-form]').forEach(bindForm);
    document.querySelectorAll('[data-cb-signup]').forEach(initializePopup);
  }

  initializeAll();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
})();
